import React, { useState, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { TieredMenu } from 'primereact/tieredmenu';

// Pre-import xlsx instead of dynamic import
import * as XLSX from 'xlsx';

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { useMountEffect } from 'primereact/hooks';
import StorageHelper from '../../utils/storageHelper';
import ChangeStatusCooperateReceiptDialog from '../../components/cooperateReceiptDialogs/ChangeStatusCooperateReceiptDialog';

const AllCooperateReceiptsTable = () => {
    const [receipts, setReceipts] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [showStatusDialog, setShowStatusDialog] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);

    const dt = useRef(null);
    const menu = useRef(null);

    useMountEffect(() => {
        fetchReceipts();
    }, []);

    const fetchReceipts = async () => {
        setLoading(true);
        try {
            const response = await axios.get(process.env.REACT_APP_DOVESAPI + 'cooperateReceipt/all', {
                headers: {
                    Authorization: 'Bearer ' + StorageHelper.get('token')
                }
            });

            if (response.data.status === "SUCCESS") {
                setReceipts(response.data.responseBody);
            } else {
                console.error('Failed to fetch receipts:', response.data.status);
            }
        } catch (error) {
            console.error('Error fetching receipts:', error);
        } finally {
            setLoading(false);
        }
    };

    const exportCSV = (selectionOnly) => {
        dt.current.exportCSV({ selectionOnly });
    };

    // Replace dynamic import with pre-imported XLSX
    const exportExcel = () => {
        try {
            const worksheet = XLSX.utils.json_to_sheet(receipts);
            const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
            const excelBuffer = XLSX.write(workbook, {
                bookType: 'xlsx',
                type: 'array'
            });
            saveAsExcelFile(excelBuffer, 'corporate-receipts-report');
        } catch (error) {
            console.error('Error exporting to Excel:', error);
        }
    };

    const saveAsExcelFile = (buffer, fileName) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName + '.xlsx');
    };

    const openStatusDialog = (receipt) => {
        setSelectedReceipt(receipt);
        setShowStatusDialog(true);
    };

    const closeStatusDialog = () => {
        setShowStatusDialog(false);
        setSelectedReceipt(null);
    };

    const handleStatusUpdate = (receiptNumber, newStatus) => {
        setReceipts(prevReceipts =>
            prevReceipts.map(receipt =>
                receipt.receiptNumber === receiptNumber
                    ? { ...receipt, status: newStatus }
                    : receipt
            )
        );
    };

    const onGlobalFilter = (event) => {
        setGlobalFilter(event.target.value);
    };

    const amountBodyTemplate = (rowData) => {
        return `${rowData.currency || ''} ${parseFloat(rowData.amount || 0).toFixed(2)}`;
    };

    const dateBodyTemplate = (rowData, field) => {
        const date = rowData[field];
        if (!date) return '-';
        return new Date(date).toLocaleString();
    };

    const statusBodyTemplate = (rowData) => {
        const getStatusStyle = (status) => {
            switch (status) {
                case 'PENDING':
                    return { color: '#FCB700', backgroundColor: '#FFF8E1' };
                case 'RECEIPTED':
                    return { color: '#00BAFE', backgroundColor: '#E6F7FF' };
                case 'COMPLETED':
                    return { color: '#00D390', backgroundColor: '#E6FFF7' };
                case 'REVOKE REQUESTED':
                    return { color: '#6C5CE7', backgroundColor: '#F3EFFF' };
                case 'FAILED':
                    return { color: '#FF6266', backgroundColor: '#FFECEC' };
                case 'REVOKED':
                    return { color: '#FF6266', backgroundColor: '#FFECEC' };
                default:
                    return { color: '#A0A0A0', backgroundColor: '#F5F5F5' };
            }
        };

        return (
            <span
                style={{
                    ...getStatusStyle(rowData.status),
                    padding: '0.25rem 0.75rem',
                    borderRadius: '0.375rem',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                }}
            >
                {rowData.status}
            </span>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <Button
                label="Change Status"
                outlined
                rounded
                onClick={() => openStatusDialog(rowData)}
                tooltip="Change Receipt Status"
                tooltipOptions={{ position: 'left' }}
                style={{
                    borderRadius: '5px',
                    whiteSpace: 'nowrap',
                }}
            />
        );
    };

    const globalFilterElement = (
        <input
            type="text"
            className="p-inputtext p-component"
            placeholder="Search receipts..."
            value={globalFilter}
            onChange={onGlobalFilter}
        />
    );

    const items = [
        {
            label: 'Download CSV',
            icon: 'pi pi-file-excel',
            command: () => { exportCSV(false); }
        },
        {
            label: 'Download Excel',
            icon: 'pi pi-file-excel',
            command: () => { exportExcel(); }
        }
    ];

    return (
        <div className="card home-chart">
            <Tooltip target=".export-buttons>button" position="bottom" />
            <div className="card-header">
                <h2 className="card-title home-chart">All Corporate Receipts Report</h2>
                <div className="flex justify-content-end">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        {globalFilterElement}
                    </span>
                </div>
                <div id="report-select">
                    <TieredMenu model={items} popup ref={menu} breakpoint="767px" />
                    <Button
                        label="Download Report"
                        icon="pi pi-download"
                        onClick={(e) => menu.current.toggle(e)}
                    />
                </div>
            </div>

            <div className="card-body">
                <DataTable
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    ref={dt}
                    value={receipts}
                    loading={loading}
                    tableStyle={{ minWidth: '50rem' }}
                    globalFilter={globalFilter}
                    emptyMessage="No corporate receipts found"
                >
                    <Column field="receiptNumber" header="Receipt Number" sortable style={{ minWidth: '150px' }} />
                    <Column field="cooperateName" header="Corporate Name" sortable style={{ minWidth: '200px' }} />
                    <Column field="modeOfPayment" header="Payment Mode" sortable style={{ minWidth: '150px' }} />
                    <Column field="serialNumber" header="Serial Number" sortable style={{ minWidth: '150px' }} />
                    <Column field="agentName" header="Agent Name" sortable style={{ minWidth: '180px' }} />
                    <Column field="agentID" header="Agent ID" sortable style={{ minWidth: '120px' }} />
                    <Column
                        field="amount"
                        header="Amount"
                        body={amountBodyTemplate}
                        sortable
                        style={{ minWidth: '150px' }}
                    />
                    <Column field="notes" header="Notes" style={{ minWidth: '200px' }} />
                    <Column
                        field="status"
                        header="Status"
                        body={statusBodyTemplate}
                        sortable
                        style={{ minWidth: '120px' }}
                    />
                    <Column
                        field="createdAt"
                        header="Created At"
                        body={(rowData) => dateBodyTemplate(rowData, 'createdAt')}
                        sortable
                        style={{ minWidth: '200px' }}
                    />
                    <Column
                        field="revokedAt"
                        header="Revoked At"
                        body={(rowData) => dateBodyTemplate(rowData, 'revokedAt')}
                        sortable
                        style={{ minWidth: '200px' }}
                    />
                    <Column
                        header="Actions"
                        body={actionBodyTemplate}
                        exportable={false}
                        style={{ minWidth: '100px', textAlign: 'center' }}
                    />
                </DataTable>
            </div>

            <ChangeStatusCooperateReceiptDialog
                visible={showStatusDialog}
                receipt={selectedReceipt}
                onHide={closeStatusDialog}
                onStatusUpdate={handleStatusUpdate}
            />
        </div>
    );
};

export default AllCooperateReceiptsTable;